import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useFeature from 'services/features/useFeature';
import { useSelector } from 'react-redux';
import { useAnalytics } from '../../contexts/analytics/AnalyticsProvider';
import useImpersonate from '../../services/auth/useImpersonate';
import { getIsImpersonatorUserAdmin } from '../../store/user';
import { useTinyAIContext } from './TinyAIContext';
import { TinyAIChatWindow } from './TinyAIChatWindow';
import { TinyAICloseButton } from './TinyAICloseButton';
import { TinyAIFloatingButton } from './TinyAIFloatingButton';

interface TinyAIProps {
  scrollableElRef?: React.RefObject<HTMLElement>;
  isHidden?: boolean;
}

// kits/activate, information, typeform and profile are the ENG-4942 activation
// flow: a floating assistant there overlaps each step's CTA and the forms.
// /kits on its own is the kit list, not the flow, so it keeps the button.
const HIDDEN_ROUTES_PATTERN =
  /^\/(refer-a-friend|account|help|guides|bookmarks|kits\/activate|information|typeform|profile)(\/|$)/;

export const TinyAI = ({ scrollableElRef, isHidden = false }: TinyAIProps) => {
  const { isChatOpen, openChat, closeChat } = useTinyAIContext();
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const { isFeatureEnabled } = useFeature('tiny_ai_chatbot_in_results_portal');
  const { trackEvent } = useAnalytics();
  const { impersonateId } = useImpersonate();
  const isImpersonatorAdmin = useSelector(getIsImpersonatorUserAdmin);
  const { pathname } = useLocation();

  useEffect(() => {
    if (isHidden && isChatOpen) {
      closeChat();
    }
  }, [isHidden, isChatOpen, closeChat]);

  if (!isFeatureEnabled || HIDDEN_ROUTES_PATTERN.test(pathname) || isHidden) {
    return null;
  }

  // Hide TinyAI for non-admin impersonators (practitioners, partners)
  if (impersonateId && !isImpersonatorAdmin) {
    return null;
  }

  const handleOpen = () => {
    openChat();
    trackEvent('TinyAI widget opened');
  };

  const handleClose = () => {
    closeChat();
    trackEvent('TinyAI widget closed');
  };

  const handleKeyboardVisibilityChange = (isVisible: boolean) => {
    setIsKeyboardVisible(isVisible);
  };

  return (
    <div>
      {!isChatOpen ? (
        <TinyAIFloatingButton
          onClick={handleOpen}
          scrollableElRef={scrollableElRef}
        />
      ) : (
        <>
          <TinyAIChatWindow
            onKeyboardVisibilityChange={handleKeyboardVisibilityChange}
            onClose={handleClose}
            scrollContainerRef={scrollableElRef}
          />
          <TinyAICloseButton
            onClick={handleClose}
            isKeyboardVisible={isKeyboardVisible}
          />
        </>
      )}
    </div>
  );
};
